import { useState, useEffect, useMemo } from 'react';
import {
  calculateOrderTimes,
  calculateValidFrom,
  isValidTimeRange,
  generateTimeOptions
} from '@/services/seller/discount/discount-time';
import { UseDiscountTimeReturn } from '@/types/discount';

interface UseDiscountTimeOptions {
  initialPickupStartTime?: string;
  initialPickupEndTime?: string;
  offerType: 'DEADLINE_DISCOUNT' | 'RESERVATION_DISCOUNT';
}

export function useDiscountTime({
  initialPickupStartTime = '18:00',
  initialPickupEndTime = '20:00',
  offerType
}: UseDiscountTimeOptions): UseDiscountTimeReturn {
  const [pickupStartTime, setPickupStartTime] = useState(initialPickupStartTime);
  const [pickupEndTime, setPickupEndTime] = useState(initialPickupEndTime);

  // props 변경 시 state 동기화
  useEffect(() => {
    setPickupStartTime(initialPickupStartTime);
    setPickupEndTime(initialPickupEndTime);
  }, [initialPickupStartTime, initialPickupEndTime]);

  const timeOptions = useMemo(() => generateTimeOptions(), []);

  const orderTimes = useMemo(() => {
    if (offerType === 'DEADLINE_DISCOUNT') {
      return calculateOrderTimes(pickupStartTime, pickupEndTime);
    }
    // 예약할인은 주문시간 계산 불필요
    return { orderStartTime: '', orderEndTime: '' };
  }, [pickupStartTime, pickupEndTime, offerType]);

  const validFrom = useMemo(() => {
    if (offerType === 'DEADLINE_DISCOUNT') {
      return calculateValidFrom(pickupStartTime);
    }
    return null;
  }, [pickupStartTime, offerType]);

  const isValidRange = useMemo(() => {
    return isValidTimeRange(pickupStartTime, pickupEndTime);
  }, [pickupStartTime, pickupEndTime]);

  const updatePickupTimes = (start: string, end: string) => {
    if (isValidTimeRange(start, end)) {
      setPickupStartTime(start);
      setPickupEndTime(end);
    }
  };

  return {
    pickupStartTime,
    pickupEndTime,
    orderStartTime: orderTimes.orderStartTime,
    orderEndTime: orderTimes.orderEndTime,
    validFrom,
    setPickupStartTime,
    setPickupEndTime,
    updatePickupTimes,
    isValidTimeRange: isValidRange,
    timeOptions
  };
}